<script setup>
import { onMounted, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactDetails from 'dashboard/components-next/Contacts/Pages/ContactDetails.vue';
import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ContactMerge from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMerge.vue';
import ContactActivityTimeline from 'dashboard/routes/dashboard/conversation/contact/ContactActivityTimeline.vue';
import ContactDeals from 'dashboard/components-next/Contacts/ContactsSidebar/ContactDeals.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';
import { LIFECYCLE_MAP } from 'dashboard/routes/dashboard/companies/constants/companyConstants';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const contact = useMapGetter('contacts/getContactById');
const uiFlags = useMapGetter('contacts/getUIFlags');

const activeTab = ref('info');

const isFetchingItem = computed(() => uiFlags.value.isFetchingItem);
const isMergingContact = computed(() => uiFlags.value.isMerging);
const isUpdatingContact = computed(() => uiFlags.value.isUpdating);

const selectedContact = computed(() => contact.value(route.params.contactId));
const contactId = computed(() => route.params.contactId);

const showSpinner = computed(
  () => isFetchingItem.value || isMergingContact.value
);

const lifecycle = computed(() => {
  const stage =
    selectedContact.value?.lifecycleStage ||
    selectedContact.value?.lifecycle_stage;
  return stage ? LIFECYCLE_MAP[stage] : null;
});

const isContactBlocked = computed(() => selectedContact.value?.blocked);

const tabs = [
  { key: 'info', label: 'Informações', icon: 'i-lucide-info' },
  { key: 'activities', label: 'Atividades', icon: 'i-lucide-activity' },
  { key: 'deals', label: 'Deals', icon: 'i-lucide-briefcase' },
  { key: 'conversations', label: 'Conversas', icon: 'i-lucide-message-square' },
  { key: 'notes', label: 'Notas', icon: 'i-lucide-sticky-note' },
  { key: 'merge', label: 'Merge', icon: 'i-lucide-git-merge' },
];

const goToContactsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/contacts?page=1`);
  }
};

const fetchActiveContact = async () => {
  if (contactId.value) {
    await store.dispatch('contacts/show', { id: contactId.value });
    await store.dispatch('contacts/fetchContactableInbox', contactId.value);
  }
};

const fetchContactNotes = () => {
  if (contactId.value) {
    store.dispatch('contactNotes/get', { contactId: contactId.value });
  }
};

const fetchContactConversations = () => {
  if (contactId.value) {
    store.dispatch('contactConversations/get', contactId.value);
  }
};

const fetchAttributes = () => {
  store.dispatch('attributes/get');
};

const toggleContactBlock = async () => {
  const blocked = isContactBlocked.value;
  try {
    await store.dispatch('contacts/update', {
      ...selectedContact.value,
      blocked: !blocked,
    });
    useAlert(
      blocked
        ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE')
        : t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE')
    );
  } catch {
    useAlert(
      blocked
        ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE')
        : t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE')
    );
  }
};

onMounted(() => {
  fetchActiveContact();
  fetchContactNotes();
  fetchContactConversations();
  fetchAttributes();
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Header -->
    <div
      class="flex items-center gap-4 px-6 py-4 border-b border-n-slate-4 bg-n-slate-1 shrink-0"
    >
      <button
        class="flex items-center gap-1 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
        @click="goToContactsList"
      >
        <span class="i-lucide-arrow-left size-4" />
        {{ t('CONTACTS_LAYOUT.HEADER.BREADCRUMB.CONTACTS') }}
      </button>
      <span class="text-n-slate-6">/</span>

      <template v-if="selectedContact && !showSpinner">
        <Avatar
          :src="selectedContact.thumbnail || ''"
          :name="selectedContact.name || ''"
          :size="36"
          rounded-full
        />
        <div class="flex-1 min-w-0">
          <h1 class="text-lg font-semibold text-n-slate-12 truncate">
            {{ selectedContact.name }}
          </h1>
        </div>
        <span
          v-if="lifecycle"
          class="text-xs font-semibold px-2 py-1 rounded-md flex-shrink-0"
          :class="lifecycle.bgClass"
        >
          {{ lifecycle.label }}
        </span>
        <div class="flex items-center gap-2 flex-shrink-0">
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4 transition-colors"
            :disabled="isUpdatingContact"
            @click="toggleContactBlock"
          >
            <span
              :class="isContactBlocked ? 'i-lucide-shield-check' : 'i-lucide-shield-ban'"
              class="size-3.5"
            />
            {{
              isContactBlocked
                ? $t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT')
                : $t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT')
            }}
          </button>
          <VoiceCallButton
            :phone="selectedContact.phoneNumber"
            :contact-id="contactId"
            :label="$t('CONTACT_PANEL.CALL')"
            size="sm"
          />
          <ComposeConversation :contact-id="contactId">
            <template #trigger="{ toggle }">
              <button
                class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-n-iris-9 text-white hover:bg-n-iris-10 transition-colors"
                @click="toggle"
              >
                <span class="i-lucide-send size-3.5" />
                {{ $t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE') }}
              </button>
            </template>
          </ComposeConversation>
        </div>
      </template>
    </div>

    <!-- Tabs -->
    <div
      class="flex gap-0 px-6 border-b border-n-slate-4 bg-n-slate-1 shrink-0"
    >
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2"
        :class="
          activeTab === tab.key
            ? 'border-n-iris-9 text-n-iris-11'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
        "
        @click="activeTab = tab.key"
      >
        <span :class="tab.icon" class="size-4" />
        {{ tab.label }}
      </button>
    </div>

    <!-- Loading -->
    <div
      v-if="showSpinner"
      class="flex items-center justify-center flex-1 p-8"
    >
      <Spinner />
    </div>

    <!-- Content -->
    <div v-else-if="selectedContact" class="flex-1 overflow-y-auto p-6">
      <!-- Info Tab -->
      <ContactDetails
        v-if="activeTab === 'info'"
        :selected-contact="selectedContact"
        @go-to-contacts-list="goToContactsList"
      />

      <!-- Activities Tab -->
      <div v-if="activeTab === 'activities'" class="max-w-5xl mx-auto">
        <ContactActivityTimeline :contact-id="contactId" />
      </div>

      <!-- Deals Tab -->
      <div v-if="activeTab === 'deals'" class="max-w-5xl mx-auto">
        <ContactDeals :contact-id="contactId" />
      </div>

      <!-- Conversations Tab -->
      <div v-if="activeTab === 'conversations'" class="max-w-5xl mx-auto">
        <ContactHistory />
      </div>

      <!-- Notes Tab -->
      <div v-if="activeTab === 'notes'" class="max-w-5xl mx-auto">
        <ContactNotes />
      </div>

      <!-- Merge Tab -->
      <div v-if="activeTab === 'merge'" class="max-w-5xl mx-auto">
        <ContactMerge
          :selected-contact="selectedContact"
          @go-to-contacts-list="goToContactsList"
          @reset-tab="activeTab = 'info'"
        />
      </div>
    </div>
  </div>
</template>
